import { Component, Inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef, MatDialogModule } from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatIconModule } from '@angular/material/icon';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { forkJoin } from 'rxjs';

import {
  DeliveryService,
  InvoiceDetails,
  PaymentRequest,
  PaymentResponse,
  CustomerBalanceResponse,
  PendingInvoiceListItem,
  CustomerCreditsResponse,
  CustomerCreditItem
} from '../../services/delivery.service';
import { MessageDialogService } from '../../services/message-dialog';

export interface CollectPaymentDialogData {
  customerId: number;
  invoiceId: number;          // required
  loadId?: number;            // optional
  defaultAmount?: number;     // optional
  defaultMethod?: PaymentRequest['method']; // optional
}

@Component({
  selector: 'app-collect-payment-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatProgressSpinnerModule,
    MatIconModule,
    MatCheckboxModule
  ],
  templateUrl: './collect-payment-dialog-component.html',
  styleUrls: ['./collect-payment-dialog-component.scss']
})
export class CollectPaymentDialogComponent implements OnInit {
  // header info
  customerName = '';
  invoiceNumber: string | null = null;
  invoiceId!: number;

  // invoice money
  currency = 'INR';
  invoiceResidual = 0;
  amount = 0;
  invoiceDiscount = 0;

  // customer balance
  hasCustomerBalance = false;
  balanceCurrency = 'INR';
  customerNetBalance = 0;
  customerTotalReceivable = 0;
  customerTotalPayable = 0;

  // payment meta
  method: PaymentRequest['method'] = 'cash';
  reference = '';
  note = '';

  // ui state
  loading = true;
  error: string | null = null;

  // invoice list state
  showInvoiceList = true;
  loadingInvoices = false;
  invoiceListError: string | null = null;
  pendingInvoices: PendingInvoiceListItem[] = [];
  filteredPendingInvoices: PendingInvoiceListItem[] = [];
  invoiceSearchTerm = '';

  // credit notes state
  creditNotes: CustomerCreditItem[] = [];
  selectedCreditIds: number[] = [];
  loadingCredits = false;
  creditsError: string | null = null;

  constructor(
    public dialogRef: MatDialogRef<CollectPaymentDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: CollectPaymentDialogData,
    private deliveryService: DeliveryService,
    private messageDialogService: MessageDialogService
  ) { }

  ngOnInit(): void {
    this.invoiceId = this.data.invoiceId;
    this.method = this.data.defaultMethod || 'cash';
    this.fetchDataAndCredits();
    this.loadPendingInvoices();
  }

  // ----- UI helpers for reference -----
  get referenceRequired(): boolean {
    return ['upi', 'bank', 'other', 'cheque'].includes(this.method);
  }
  get showReference(): boolean {
    return this.method !== 'cash';
  }
  get referenceLabel(): string {
    switch (this.method) {
      case 'upi': return 'UPI Ref / UTR';
      case 'bank': return 'Bank Ref / Transaction ID';
      case 'cheque': return 'Cheque No.';
      default: return 'Reference';
    }
  }
  get referencePlaceholder(): string {
    switch (this.method) {
      case 'upi': return 'e.g. UTR123...';
      case 'bank': return 'e.g. NEFT/IMPS/RTGS ref';
      case 'cheque': return 'e.g. CHQ-00123';
      default: return 'Optional reference';
    }
  }
  onMethodChange() {
    if (!this.reference && this.referenceRequired) {
      this.reference = '';
    }
  }

  // ----- data fetch (invoice + customer balance + credits in parallel) -----
  private fetchDataAndCredits() {
    this.loading = true;
    this.loadingCredits = true;
    this.error = null;
    this.creditsError = null;

    forkJoin({
      invoice: this.deliveryService.getInvoice(this.invoiceId),
      balance: this.deliveryService.getCustomerBalance(this.data.customerId),
      credits: this.deliveryService.getOpenCreditsForCustomer(this.data.customerId)
    }).subscribe({
      next: ({ invoice, balance, credits }) => {
        // invoice
        this.customerName = invoice.partnerName || '';
        this.invoiceNumber = invoice.name || null;
        this.currency = invoice.currency || 'INR';
        this.invoiceResidual = invoice.totals?.amountResidual ?? 0;

        // customer balance (non-fatal if missing)
        if (balance.status === 'success' && balance.balance) {
          this.hasCustomerBalance = true;
          this.balanceCurrency = balance.balance.currency || this.currency || 'INR';
          this.customerNetBalance = balance.balance.netBalance || 0;
          this.customerTotalReceivable = balance.balance.totalReceivable || 0;
          this.customerTotalPayable = balance.balance.totalPayable || 0;
        } else {
          this.hasCustomerBalance = false;
        }

        // credits
        if (credits.status === 'success') {
          this.creditNotes = credits.credits || [];
          // Select all credits by default
          this.selectedCreditIds = this.creditNotes.map(credit => credit.id);
        } else {
          this.creditsError = credits.message || 'Failed to load credit notes';
        }

        // Set initial amount after all data is loaded
        const def = this.data.defaultAmount;
        const baseAmount = def && def > 0 ? def : this.invoiceResidual;
        this.amount = Math.min(this.getAdjustedAmount(), baseAmount);

        this.loading = false;
        this.loadingCredits = false;
      },
      error: (err) => {
        this.loading = false;
        this.loadingCredits = false;
        this.error = err?.message || 'Failed to load data';
      }
    });
  }

  // ----- actions -----
  onCancel() {
    this.dialogRef.close();
  }

  canSubmit(): boolean {
    if (this.loading || !!this.error) return false;
    if (!this.invoiceId) return false;
    if (!this.method) return false;
    if (this.amount <= 0 || this.amount > this.getMaxAmount()) return false;
    if (this.referenceRequired && !this.reference?.trim()) return false;
    return true;
  }

  getMaxAmount(): number {
    return this.getAdjustedResidual();
  }

  getSelectedCreditsTotal(): number {
    return this.selectedCreditIds.reduce((total, creditId) => {
      const credit = this.creditNotes.find(c => c.id === creditId);
      return total + (credit?.openAmount || 0);
    }, 0);
  }

  getAdjustedAmount(): number {
    return this.getAdjustedResidual();
  }

  private getAdjustedResidual(): number {
    return Math.max(0, this.invoiceResidual - this.getSelectedCreditsTotal() - Number(this.invoiceDiscount || 0));
  }

  onCollect() {
    if (!this.canSubmit()) return;

    // Show confirmation dialog
    const confirmMessage = `Are you sure you want to collect this payment?\n\n` +
      `Customer: ${this.customerName}\n` +
      `Invoice: ${this.invoiceNumber || this.invoiceId}\n` +
      `Amount: ${this.amount.toFixed(2)} ${this.currency}\n` +
      `Method: ${this.method.toUpperCase()}\n` +
      `${this.invoiceDiscount > 0 ? `Discount: ${this.invoiceDiscount.toFixed(2)} ${this.currency}\n` : ''}` +
      `${this.reference ? `Reference: ${this.reference}\n` : ''}` +
      `${this.selectedCreditIds.length > 0 ? `Credits Applied: ${this.selectedCreditIds.length}\n` : ''}`;

    this.messageDialogService.confirm(confirmMessage, 'Confirm Payment Collection').subscribe(confirmed => {
      if (confirmed) {
        const payload: PaymentRequest = {
          invoice_id: this.invoiceId,
          amount: Number(this.amount),
          invoice_discount: Number(this.invoiceDiscount || 0),
          method: this.method,
          reference: this.reference?.trim() || undefined,
          note: this.note?.trim() || undefined,
          load_id: this.data.loadId,
          credit_note_ids: this.selectedCreditIds.length > 0 ? this.selectedCreditIds : undefined
        };

        this.loading = true;
        this.deliveryService.createPayment(payload).subscribe({
          next: (res: PaymentResponse) => {
            this.loading = false;
            this.dialogRef.close(res); // return full payment payload
          },
          error: (err) => {
            this.loading = false;
            this.error = err?.error?.message || err?.message || 'Failed to create payment';
          }
        });
      }
    });
  }

  onInvoiceDiscountChange() {
    const max = this.getAdjustedAmount();
    if (this.amount > max) {
      this.amount = max;
    }
    if (this.amount < 0) {
      this.amount = 0;
    }
  }

  // ----- invoice list methods -----
  toggleInvoiceList() {
    this.showInvoiceList = !this.showInvoiceList;
    if (this.showInvoiceList && this.pendingInvoices.length === 0) {
      this.loadPendingInvoices();
    }
  }

  private loadPendingInvoices() {
    this.loadingInvoices = true;
    this.invoiceListError = null;

    this.deliveryService.getPendingInvoicesForCustomer(this.data.customerId).subscribe({
      next: (response) => {
        if (response.status === 'success') {
          this.pendingInvoices = response.invoices || [];
          this.filteredPendingInvoices = [...this.pendingInvoices];
        } else {
          this.invoiceListError = response.message || 'Failed to load invoices';
        }
        this.loadingInvoices = false;
      },
      error: (err) => {
        this.loadingInvoices = false;
        this.invoiceListError = err?.error?.message || err?.message || 'Failed to load invoices';
      }
    });
  }

  filterInvoices() {
    if (!this.invoiceSearchTerm.trim()) {
      this.filteredPendingInvoices = [...this.pendingInvoices];
      return;
    }

    const term = this.invoiceSearchTerm.toLowerCase();
    this.filteredPendingInvoices = this.pendingInvoices.filter(invoice => 
      invoice.invoiceNumber?.toLowerCase().includes(term) ||
      invoice.invoiceId.toString().includes(term)
    );
  }

  selectInvoice(invoice: PendingInvoiceListItem) {
    this.invoiceId = invoice.invoiceId;
    this.invoiceNumber = invoice.invoiceNumber;
    this.invoiceResidual = invoice.amountResidual;
    this.currency = invoice.currency || 'INR';
    
    // Reset amount to the new invoice's residual
    this.amount = Math.min(this.invoiceResidual, this.data.defaultAmount && this.data.defaultAmount > 0 ? this.data.defaultAmount : this.invoiceResidual);
    
    // Close the invoice list
    this.showInvoiceList = false;
    this.invoiceSearchTerm = '';
    this.filteredPendingInvoices = [...this.pendingInvoices];
  }

  getPaymentStateClass(paymentState: string): string {
    switch (paymentState) {
      case 'not_paid': return 'not-paid';
      case 'partial': return 'partial';
      case 'in_payment': return 'in-payment';
      case 'paid': return 'paid';
      default: return '';
    }
  }

  getPaymentStateText(paymentState: string): string {
    switch (paymentState) {
      case 'not_paid': return 'Not Paid';
      case 'partial': return 'Partial';
      case 'in_payment': return 'In Payment';
      case 'paid': return 'Paid';
      default: return paymentState;
    }
  }

  // ----- credit notes methods -----

  onCreditSelectionChange(creditId: number, isSelected: boolean) {
    if (isSelected) {
      if (!this.selectedCreditIds.includes(creditId)) {
        this.selectedCreditIds.push(creditId);
      }
    } else {
      this.selectedCreditIds = this.selectedCreditIds.filter(id => id !== creditId);
    }
    
    // Auto-adjust amount when credits are selected
    this.amount = this.getAdjustedAmount();
  }

  isCreditSelected(creditId: number): boolean {
    return this.selectedCreditIds.includes(creditId);
  }
}
